import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Pin, PinOff, Wrench, Newspaper } from 'lucide-react'; // Added Newspaper
import { tools } from '../lib/tools'; // Import shared tools
import ThemeToggle from './ThemeToggle';

// Removed local navItems definition

function Sidebar() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('toolFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('toolFavorites', JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (path: string) => {
    setFavorites(prev => {
      if (prev.includes(path)) {
        return prev.filter(p => p !== path);
      }
      return [...prev, path];
    });
  };

  // Separate favorites and regular items
  const favoriteItems = tools.filter(item => favorites.includes(item.path));
  const regularItems = tools.filter(item => !favorites.includes(item.path));

  const renderNavItem = (item: typeof tools[0], isFavorite: boolean = false) => (
    <Link
      key={item.path}
      to={item.path}
      className={`flex items-center px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden ${
        location.pathname === item.path
          ? 'bg-[#1E3A8A] text-white shadow-lg'
          : 'text-gray-400 hover:bg-white/[0.03] hover:text-[#60A5FA]'
      }`}
      title={isCollapsed ? item.label : undefined}
      aria-current={location.pathname === item.path ? 'page' : undefined}
    >
      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.05] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
      
      {/* Icon and Label */}
      <div className="relative flex items-center flex-1 min-w-0">
        <item.icon className={`h-5 w-5 transition-colors duration-300 ${
          location.pathname === item.path
            ? 'text-white'
            : 'text-gray-400 group-hover:text-[#60A5FA]'
        } ${isCollapsed ? 'mx-auto' : 'mr-3'}`} />
        
        
        {!isCollapsed && (
          <span className="font-medium whitespace-nowrap truncate flex-1">
            {item.label}
          </span>
        )}
      </div>

      {/* Favorite Toggle */}
      {!isCollapsed && item.path !== '/' && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(item.path);
          }}
          className="ml-2 p-1 text-gray-400 hover:text-yellow-400 transition-colors opacity-0 group-hover:opacity-100"
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          {isFavorite ? (
            <PinOff className="h-4 w-4" />
          ) : (
            <Pin className="h-4 w-4" />
          )}
        </button>
      )}
    </Link>
  );

  return (
    <div className={`hidden md:flex flex-col bg-[#111111] border-r border-white/[0.05] min-h-screen transition-all duration-300 ${
      isCollapsed ? 'w-20' : 'w-64'
    }`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-white/[0.05]">
        <Link 
          to="/" 
          className={`flex items-center space-x-2 group ${isCollapsed ? 'justify-center' : ''}`}
          aria-label="Go to tools menu"
        >
          <Wrench className="h-6 w-6 text-gray-400 transition-colors duration-300 group-hover:text-[#60A5FA]" />
          {!isCollapsed && (
            <span className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-gray-200 to-gray-400 transition-colors duration-300 group-hover:from-white group-hover:to-gray-200">
              Tools Menu
            </span>
          )}
        </Link>
        <ThemeToggle />
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 text-gray-400 hover:text-gray-300 transition-colors rounded-lg hover:bg-white/[0.05]"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <Menu className="h-5 w-5" />
          ) : (
            <X className="h-5 w-5" />
          )}
        </button>
      </div>
      
      {/* Navigation */}
      <nav 
        className="flex-1 px-2 py-4 space-y-1 overflow-y-auto scrollbar-hide"
        aria-label="Main navigation"
      >
        {/* Static Blog Link First */}
        <Link
          key="/blog"
          to="/blog"
          className={`flex items-center px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden ${
            location.pathname.startsWith('/blog')
              ? 'bg-[#1E3A8A] text-white shadow-lg'
              : 'text-gray-400 hover:bg-white/[0.03] hover:text-[#60A5FA]'
          }`}
          title={isCollapsed ? "Blog" : undefined}
          aria-current={location.pathname.startsWith('/blog') ? 'page' : undefined}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.05] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
          <div className="relative flex items-center flex-1 min-w-0">
            <Newspaper className={`h-5 w-5 transition-colors duration-300 ${
              location.pathname.startsWith('/blog')
                ? 'text-white'
                : 'text-gray-400 group-hover:text-[#60A5FA]'
            } ${isCollapsed ? 'mx-auto' : 'mr-3'}`} />
            {!isCollapsed && (
              <span className="font-medium whitespace-nowrap truncate flex-1">
                Blog
              </span>
            )}
          </div>
        </Link>

        {/* Favorites Section */}
        {favoriteItems.length > 0 && (
          <div className="space-y-1 mb-4">
            {!isCollapsed && (
              <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Favorites
              </div>
            )}
            {favoriteItems.map(item => renderNavItem(item, true))}
            {!isCollapsed && <div className="border-b border-gray-700/50 my-4" />}
          </div>
        )}

        {/* Regular Items */}
        {regularItems.map(item => renderNavItem(item))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/[0.05]">
        <div className={`text-xs text-gray-500 ${isCollapsed ? 'text-center' : ''}`}>
          © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;